import { useEffect, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from './redux';
import { localApi } from '../store/api/localApi';
import type { RootState } from '../store';
import { Thread } from '../types';

interface FamilyThreads {
  threadsByChild: Record<string, Thread[]>;
  isLoading: boolean;
}

// createSelector throws on an empty input list, so a family with no children
// reads this constant (stable identity: no re-render churn).
const NO_THREADS: FamilyThreads = { threadsByChild: {}, isLoading: false };
const selectNoThreads = (_state: RootState) => NO_THREADS;

/**
 * Every thread for a set of children. RTK Query hooks cannot be called in a
 * loop, so this subscribes one `listStudentThreads` query per child in an
 * effect and reads all of them through a single memoised selector.
 */
export function useFamilyThreads(childIds: string[]): FamilyThreads {
  const dispatch = useAppDispatch();
  const key = childIds.join(',');
  useEffect(() => {
    const subs = childIds.map((id) => dispatch(localApi.endpoints.listStudentThreads.initiate(id)));
    return () => subs.forEach((s) => s.unsubscribe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, dispatch]);

  const selector = useMemo(
    () =>
      childIds.length === 0
        ? selectNoThreads
        : createSelector(
            childIds.map((id) => localApi.endpoints.listStudentThreads.select(id)),
            (...results): FamilyThreads => {
              const threadsByChild: Record<string, Thread[]> = {};
              let isLoading = false;
              results.forEach((r, i) => {
                threadsByChild[childIds[i]] = r.data?.threads ?? [];
                if (r.isUninitialized || r.isLoading) isLoading = true;
              });
              return { threadsByChild, isLoading };
            }
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  );
  return useAppSelector(selector);
}
